import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useAutomationSuggestionsSidebar } from 'Hooks/useRefreshEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';

const AutomationSuggestionsContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const AUTOMATION_SUGGESTIONS_REFRESH_STARTED = 'automation-suggestions-started';
const AUTOMATION_SUGGESTIONS_REFRESH_SUCCEEDED = 'automation-suggestions-succeeded';
const AUTOMATION_SUGGESTIONS_REFRESH_FAILED = 'automation-suggestions-refresh-failed';


const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    automationSuggestionsCollection: null,
    isAutomationSuggestionsRefreshing: false,
    automationSuggestionsRefreshError: null,
    // automationSuggestionsCollection: null,
    isOptimisticallyUpdating: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        automationSuggestionsCollection: action.automationSuggestionsCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case AUTOMATION_SUGGESTIONS_REFRESH_STARTED: {
      return {
        ...state,
        isAutomationSuggestionsRefreshing: true,
        automationSuggestionsRefreshError: null,
      };
    }
    case AUTOMATION_SUGGESTIONS_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isAutomationSuggestionsRefreshing: false,
        automationSuggestionsRefreshError: null,
        automationSuggestionsCollection: action.automationSuggestionsCollection,
        // automationSuggestionssCollection: action.automationSuggestionssCollection,
      };
    }
    case AUTOMATION_SUGGESTIONS_REFRESH_FAILED: {
      return {
        ...state,
        isAutomationSuggestionsRefreshing: false,
        automationSuggestionsRefreshError: action.automationSuggestionsRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled AutomationSuggestionsContext action type: ${action.type}`);
    }
  }
};

const AutomationSuggestionsContextProvider = ({
  relationship,
  children,
}) => {
  const { pageContentResource } = usePageContentContext();

  const { displayErrorMessage } = useSnackbarContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshAutomationSuggestionsCollection = React.useCallback(async () => {
    const { automationSuggestionsCollection: automationSuggestionsColl } = state;

    if (automationSuggestionsColl === null) {
      return;
    }

    if (automationSuggestionsColl.resources().length() === 0) {
      return;
    }

    const [automationSuggestionsRes] = automationSuggestionsColl;

    try {
      dispatch({ type: AUTOMATION_SUGGESTIONS_REFRESH_STARTED });
      const coll = await automationSuggestionsRes.link('self').get();

      dispatch({
        type: AUTOMATION_SUGGESTIONS_REFRESH_SUCCEEDED,
        automationSuggestionsCollection: coll,
      });
    } catch (err) {
      dispatch({ type: AUTOMATION_SUGGESTIONS_REFRESH_FAILED, automationSuggestionsRefreshError: err });
    }
  }, [state]);

  const loadCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await relationship.link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        automationSuggestionsCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadCollection);

  useAutomationSuggestionsSidebar(async () => {
    await loadCollection();
  }, []);

  // Refresh the groups whenever the page is loading
  React.useEffect(() => {
    refreshAutomationSuggestionsCollection().catch(err => {
      displayErrorMessage(err, 'Failed to refresh desired outcomes collection');
    });
  }, [pageContentResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      automationSuggestionsCollection: state.automationSuggestionsCollection,
      automationSuggestionsResource: state.automationSuggestionsCollection?.resources().head(), // collection is null to start
      isAutomationSuggestionsRefreshing: state.isAutomationSuggestionsRefreshing,
      automationSuggestionsRefreshError: state.automationSuggestionsRefreshError,
      isOptimisticallyUpdating: state.isOptimisticallyUpdating,
      refreshAutomationSuggestionsCollection,
    };
  }, [
    state,
    refreshAutomationSuggestionsCollection,
  ]);

  return (
    <AutomationSuggestionsContext.Provider value={memoizedValue}>
      {children}
    </AutomationSuggestionsContext.Provider>
  );
};

// Use AutomationSuggestions State Context
const useAutomationSuggestionsContext = () => {
  const context = React.useContext(AutomationSuggestionsContext);
  if (context === undefined) {
    throw new Error('useAutomationSuggestionsContext must be used within a AutomationSuggestionsContextProvider');
  }
  return context;
};

AutomationSuggestionsContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object.isRequired,
};

export {
  AutomationSuggestionsContextProvider,
  useAutomationSuggestionsContext,
};
